import {
  Badge,
  Datatable,
  DatatableBody,
  DatatableCell,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
  Skeleton,
} from '@monksflow/monks-ui';
import { CircleAlertIcon } from 'lucide-react';
import {
  effectiveVersion,
  hasOpenDraft,
  GUIDELINE_LEVEL_LABELS,
  GUIDELINE_STATUS_LABELS,
  GUIDELINE_TIER_LABELS,
  isEnforced,
  type MediaGuideline,
} from '@/entities/media-guideline';
import { TIER_VARIANT } from '../lib/enforcement';
import { TABLE_COL_WIDTH } from '@/shared/constants';
import { TruncatedText } from '@/shared/ui/TruncatedText';
import { formatShortDate } from '@/shared/utils/format';
import { GuidelineRowActions } from './GuidelineRowActions';
import { useTranslation } from 'react-i18next';

/** Tier is the thing a reader scans for, so it carries the only colour in the row. */
const SKELETON_ROWS = Array.from({ length: 5 }, (_, i) => i);
const TOTAL_COLUMNS = 7;

interface MediaGuidelinesTableProps {
  rows: MediaGuideline[];
  isLoading: boolean;
  onEdit: (guideline: MediaGuideline) => void;
  onPublish: (guideline: MediaGuideline) => void;
  onRetire: (guideline: MediaGuideline) => void;
  onDelete: (guideline: MediaGuideline) => void;
}

/**
 * The workspace's house standards, laid out like Brand & Visual Guidelines.
 *
 * Built on the `Datatable` primitives rather than copied from that screen, which
 * is still on MUI: this app is migrating off it, so an existing MUI table is
 * legacy rather than the pattern to follow.
 */
export function MediaGuidelinesTable({
  rows,
  isLoading,
  onEdit,
  onPublish,
  onRetire,
  onDelete,
}: MediaGuidelinesTableProps) {
  const { t } = useTranslation('knowledge');
  const isBusy = isLoading && rows.length === 0;

  return (
    <Datatable aria-busy={isBusy || undefined} className="[&_thead_th]:border-b">
      <DatatableHeader>
        <DatatableRow>
          <DatatableHead scope="col">
            {t('mediaGuidelines.mediaGuidelinesTable.name')}
          </DatatableHead>
          <DatatableHead scope="col" className={TABLE_COL_WIDTH.type}>
            {t('mediaGuidelines.mediaGuidelinesTable.level')}
          </DatatableHead>
          <DatatableHead scope="col" className={TABLE_COL_WIDTH.statusBadge}>
            {t('mediaGuidelines.mediaGuidelinesTable.tier')}
          </DatatableHead>
          <DatatableHead scope="col" className={TABLE_COL_WIDTH.type}>
            {t('mediaGuidelines.mediaGuidelinesTable.category')}
          </DatatableHead>
          <DatatableHead scope="col" className={TABLE_COL_WIDTH.statusBadge}>
            {t('mediaGuidelines.mediaGuidelinesTable.status')}
          </DatatableHead>
          <DatatableHead scope="col" className={TABLE_COL_WIDTH.dateText}>
            {t('mediaGuidelines.mediaGuidelinesTable.verified')}
          </DatatableHead>
          <DatatableHead scope="col" className={TABLE_COL_WIDTH.actions}>
            <span className="sr-only">{t('mediaGuidelines.mediaGuidelinesTable.actions')}</span>
          </DatatableHead>
        </DatatableRow>
      </DatatableHeader>
      <DatatableBody>
        {isBusy ? (
          SKELETON_ROWS.map((i) => (
            <DatatableRow key={i} className="h-20">
              {Array.from({ length: TOTAL_COLUMNS }, (_, cell) => (
                <DatatableCell key={cell}>
                  <Skeleton className="h-4 w-full" />
                </DatatableCell>
              ))}
            </DatatableRow>
          ))
        ) : rows.length === 0 ? (
          <DatatableRow>
            <DatatableCell
              colSpan={TOTAL_COLUMNS}
              className="h-20 text-center text-muted-foreground"
            >
              {t('mediaGuidelines.mediaGuidelinesTable.noGuidelinesYet')}
            </DatatableCell>
          </DatatableRow>
        ) : (
          rows.map((guideline) => {
            const version = effectiveVersion(guideline);
            const enforced = isEnforced(guideline);

            return (
              <DatatableRow
                key={guideline.id}
                interactive
                className="h-20"
                onClick={() => onEdit(guideline)}
              >
                <DatatableCell>
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <TruncatedText
                      value={guideline.name}
                      className="text-sm font-medium text-foreground"
                    />
                    {guideline.description && (
                      <TruncatedText
                        value={guideline.description}
                        className="text-xs text-muted-foreground"
                      />
                    )}
                  </div>
                </DatatableCell>

                <DatatableCell>
                  <span className="text-sm">
                    {version ? GUIDELINE_LEVEL_LABELS[version.level] : '—'}
                  </span>
                </DatatableCell>

                <DatatableCell>
                  {version ? (
                    <Badge
                      variant={TIER_VARIANT[version.tier]}
                      roundness="full"
                      className="font-normal"
                    >
                      {GUIDELINE_TIER_LABELS[version.tier]}
                    </Badge>
                  ) : (
                    <span className="text-sm text-muted-foreground">—</span>
                  )}
                </DatatableCell>

                <DatatableCell>
                  {/* Null means it applies everywhere, which is a value, not a gap. */}
                  <span className="text-sm">
                    {guideline.campaignCategory ??
                      t('mediaGuidelines.mediaGuidelinesTable.allCategories')}
                  </span>
                </DatatableCell>

                <DatatableCell>
                  {enforced ? (
                    <Badge variant="secondary" roundness="full" className="font-normal">
                      {GUIDELINE_STATUS_LABELS[guideline.status]}
                    </Badge>
                  ) : (
                    // An unpublished guideline is not checking anything, which the
                    // status column alone would not tell you.
                    <Badge variant="outline" roundness="full" className="font-normal">
                      {guideline.status === 'active'
                        ? t('mediaGuidelines.mediaGuidelinesTable.draft')
                        : GUIDELINE_STATUS_LABELS[guideline.status]}
                    </Badge>
                  )}
                </DatatableCell>

                <DatatableCell>
                  {guideline.lastVerifiedAt ? (
                    <span className="text-sm">{formatShortDate(guideline.lastVerifiedAt)}</span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1 text-sm text-muted-foreground"
                      title={t('mediaGuidelines.mediaGuidelinesTable.neverVerified')}
                    >
                      <CircleAlertIcon className="size-4" />
                      {t('mediaGuidelines.mediaGuidelinesTable.never')}
                    </span>
                  )}
                </DatatableCell>

                <DatatableCell className="w-12">
                  <GuidelineRowActions
                    guideline={guideline}
                    hasOpenDraft={hasOpenDraft(guideline)}
                    onEdit={onEdit}
                    onPublish={onPublish}
                    onRetire={onRetire}
                    onDelete={onDelete}
                  />
                </DatatableCell>
              </DatatableRow>
            );
          })
        )}
      </DatatableBody>
    </Datatable>
  );
}
